
import {
  defineComponent, PropType, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Loader from '@/components/layout/Loader.vue';
import type { ImportFieldMapping, ConsentAllOptions, SelectableItem } from '@/composables/import/types';

export default defineComponent({
  name: 'ImportPreview',
  components: {
    Button,
    Loader,
  },

  props: {
    fileName: {
      type: String,
      default: '',
    },

    totalRows: {
      type: Number,
      required: true,
    },

    mapping: {
      type: Array as PropType<ImportFieldMapping[]>,
      required: true,
    },

    submitting: {
      type: Boolean,
      default: false,
    },

    hideNavigation: {
      type: Boolean,
      default: false,
    },

    /** Import type (contact, custom_data, etc.) */
    importType: {
      type: String,
      default: '',
    },

    /** Consent options (v-model) */
    consentAll: {
      type: Object as PropType<ConsentAllOptions>,
      default: () => ({}),
    },

    /** Selected items for item selector imports (automation/bulk/template) */
    selectedItems: {
      type: Array as PropType<SelectableItem[]>,
      default: () => [],
    },

    /** Selected VAPID key label (for push notification import) */
    vapidKeyLabel: {
      type: String,
      default: '',
    },
  },

  emits: ['submit', 'back', 'update:consentAll'],

  setup(props, { emit }) {
    const { t, te } = useI18n();

    // Check if email is mapped but email_consent is not
    const showEmailConsentQuestion = computed(() => {
      if (props.importType !== 'contact') return false;

      const mappedTargets = props.mapping.map((m) => m.target);
      const hasEmail = mappedTargets.includes('email');
      const hasEmailConsent = mappedTargets.includes('email_consent');

      return hasEmail && !hasEmailConsent;
    });

    // Check if phone_number is mapped but sms_consent is not
    const showSmsConsentQuestion = computed(() => {
      if (props.importType !== 'contact') return false;

      const mappedTargets = props.mapping.map((m) => m.target);
      const hasPhone = mappedTargets.includes('phone_number');
      const hasSmsConsent = mappedTargets.includes('sms_consent');

      return hasPhone && !hasSmsConsent;
    });

    // Check if phone_number is mapped but whatsapp_consent is not
    const showWhatsappConsentQuestion = computed(() => {
      if (props.importType !== 'contact') return false;

      const mappedTargets = props.mapping.map((m) => m.target);
      const hasPhone = mappedTargets.includes('phone_number');
      const hasWhatsappConsent = mappedTargets.includes('whatsapp_consent');

      return hasPhone && !hasWhatsappConsent;
    });

    // Check if any consent is enabled
    const hasAnyConsentEnabled = computed(() => props.consentAll?.email || props.consentAll?.sms || props.consentAll?.whatsapp);

    // Check if this is an item selector import (automation/bulk/template)
    const isItemSelectorImport = computed(() => ['campaign_automation', 'campaign_bulk', 'template', 'stats_report'].includes(props.importType));

    // Get the label for items count based on import type
    const itemsCountLabel = computed(() => {
      const count = props.selectedItems.filter((item) => item.selected).length;
      switch (props.importType) {
        case 'campaign_automation':
          return t('import.preview.itemsToImport.automation', { count });
        case 'campaign_bulk':
          return t('import.preview.itemsToImport.bulk', { count });
        case 'template':
          return t('import.preview.itemsToImport.template', { count });
        case 'stats_report':
          return t('import.preview.itemsToImport.stats_report', { count });
        default:
          return t('import.preview.rowsToImport', { count });
      }
    });

    // Get icon for import type
    const getImportTypeIcon = computed(() => {
      switch (props.importType) {
        case 'campaign_automation':
          return 'fas fa-robot';
        case 'campaign_bulk':
          return 'fas fa-paper-plane';
        case 'template':
          return 'fas fa-palette';
        default:
          return 'fas fa-file';
      }
    });

    // Get only selected items for display
    const filteredSelectedItems = computed(() => props.selectedItems.filter((item) => item.selected));

    // Update consent value directly
    const updateConsent = (type: 'email' | 'sms' | 'whatsapp', value: boolean) => {
      const newConsent = { ...props.consentAll, [type]: value };
      emit('update:consentAll', newConsent);
    };

    // Get translated label for target field
    const getTargetLabel = (target: string): string => {
      if (props.importType === 'contact') {
        const translationKey = `import.contact.fields.${target}.label`;
        // Check if translation exists before using it
        if (te(translationKey)) {
          return t(translationKey);
        }
      }
      // Fallback to raw value
      return target;
    };

    return {
      t,
      showEmailConsentQuestion,
      showSmsConsentQuestion,
      showWhatsappConsentQuestion,
      hasAnyConsentEnabled,
      isItemSelectorImport,
      itemsCountLabel,
      getImportTypeIcon,
      filteredSelectedItems,
      updateConsent,
      getTargetLabel,
    };
  },
});
